import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, Settings, Search, LogOut, Check, CircleHelp } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { useSiteSelector } from "@/hooks/useSiteSelector";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import ConcretlyLogo from "@/assets/concretly-logo.svg";

type SidebarHeaderMenuVariant = 'site_manager' | 'project_supervisor' | 'company_director';

interface SidebarHeaderMenuProps {
  variant?: SidebarHeaderMenuVariant;
}

export type { SidebarHeaderMenuVariant };

export function SidebarHeaderMenu({ variant = 'company_director' }: SidebarHeaderMenuProps) {
  const isSiteManager = variant === 'site_manager';
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { sites, selectedSite, selectSite } = useSiteSelector();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const companyName = user?.company_name || "Mon entreprise";
  const userName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email?.split("@")[0] || "Utilisateur";
  const userEmail = user?.email || "";

  const handleSiteSelect = (site: { id: string; name: string; location: string; status?: string }) => {
    selectSite(site);
    if (isSiteManager) {
      navigate("/site");
    } else {
      navigate(`/sites/${site.short_code ?? site.id}`);
    }
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      const { error } = await signOut();
      if (error) {
        toast.error("Erreur lors de la déconnexion");
      } else {
        toast.success("Déconnexion réussie");
      }
      navigate("/auth");
    } catch {
      toast.error("Erreur lors de la déconnexion");
      navigate("/auth");
    } finally {
      setIsLoggingOut(false);
      setShowLogoutDialog(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground w-full"
          >
            <img
              src={ConcretlyLogo}
              alt="Concretly"
              className="h-6 w-6 shrink-0 mr-md"
            />
            <div className="grid flex-1 text-left leading-tight overflow-hidden">
              <span className="text-label text-foreground truncate">
                {companyName}
              </span>
              <span className="text-body-sm text-muted-foreground truncate">
                {isSiteManager ? (selectedSite?.name || "Aucun chantier") : userName}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          side="bottom"
          align="start"
          sideOffset={4}
        >
          <DropdownMenuGroup>
            <div className="px-sm py-2xs text-body-sm text-muted-foreground">Connecté en tant que</div>
            <div className="px-sm pb-2xs">
              <div className="text-label text-foreground truncate">{userName}</div>
              <div className="text-helper text-muted-foreground truncate">{userEmail}</div>
            </div>
          </DropdownMenuGroup>

          {isSiteManager && (
            <>
              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuLabel>Chantier actif</DropdownMenuLabel>
                {sites.map((site) => (
                  <DropdownMenuItem
                    key={site.id}
                    onClick={() => handleSiteSelect(site)}
                    className="gap-sm"
                  >
                    <span className="flex-1 truncate">{site.name}</span>
                    {selectedSite?.id === site.id && (
                      <Check className="size-4 shrink-0 text-muted-foreground" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => navigate(isSiteManager ? "/site/help" : "/help")}>
              <CircleHelp className="mr-2 h-4 w-4" />
              Aide
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              Paramètres
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                window.dispatchEvent(
                  new KeyboardEvent("keydown", { key: "k", metaKey: true })
                );
              }}
            >
              <Search className="mr-2 h-4 w-4" />
              Recherche
              <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-3xs rounded border border-border bg-background px-2xs font-mono text-body-sm text-muted-foreground">
                <span className="text-body-sm">⌘</span>K
              </kbd>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={() => setShowLogoutDialog(true)}
              variant="destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la déconnexion</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir vous déconnecter ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut} disabled={isLoggingOut}>
              {isLoggingOut ? "Déconnexion..." : "Se déconnecter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
